import CallDispositionApi, { UpdateParams } from 'resources/CallDisposition/Api';
import { VoiceCallDisposition } from 'resources/CallDisposition/types';
import { getUnacknowledgedMissedCallCount } from 'resources/CallLogs/Data';
import { VoiceDevice } from 'resources/Voice/Devices/types';
import { VoicemailUuid } from 'resources/Voice/types';
import VoicemailsApi from 'resources/Voice/VoicemailsApi';
import { getUnlistenedCount } from 'resources/Voice/VoicemailsData';
import VoiceSettingsApi from 'resources/Voice/VoiceSettingsApi';
import { ENTITY_CALL_DISPOSITIONS, ENTITY_CALL_LOGS, ENTITY_CALL_LOGS_LIST } from 'schema/CallLogsSchema';
import { ENTITY_VOICEMAILS } from 'schema/VoicemailSchema';
import { ENTITY_VOICE_SETTINGS, VOICE_SETTINGS_ID_ATTRIBUTE } from 'schema/VoiceSettingsSchema';

import { CALL_LOGS_PAGINATION_ID } from 'components/CallTracking/Constants';
import { receivedServerData } from 'components/Common/Data/DataFetcher';
import { invalidateEntities, updateEntity } from 'components/Common/Data/DataFetcherEntitiesHelper';
import { AppDispatch } from 'components/Common/Data/StoreCreator';
import { track } from 'components/Services/Analytics/AnalyticsService';
import { trackButtonClicked } from 'components/Services/Analytics/TrackEventsService';

import VoiceApi from './Api';

export { launchCifPage } from 'components/CallTracking/Actions';

interface UpdateRecordingParams {
  callSid: string;
  isToggledOn: boolean;
  displayDirection: string | undefined;
}
interface UpdateConferenceRecordingParams {
  callSid: string;
  isToggledOn: boolean;
}
interface CallFlowWidgetMap {
  [key: string]: string;
}

const DIALER_BUTTON_LABEL = 'dialer';
const KEYPAD_BUTTON_LABEL = 'keypad';
const KEYPAD_DIALOG_BUTTON_LABEL = 'keypad dialog';
const CALL_LOG_BUTTON_LABEL = 'call log';
const ACTIVE_DIALER_LABEL = 'active dialer';
const ACTIVE_DIALER_KEYPAD_LABEL = 'active dialer keypad';
const ADD_CALL_DIALER_TRANSFER_LABEL = 'add call';
const CONFERENCE_BUTTON_LABEL = 'conference';
const CALL_LOG_NAVIGATION_BUTTON_NAME = 'call log';
const VOICEMAILS_NAVIGATION_BUTTON_NAME = 'voicemails';
const KEYPAD_BUTTON_NAME = 'keypad';
const KEYPAD_CLOSE_BUTTON_NAME = 'x';
const CALL_QUEUE_BUTTON_NAME = 'call queue';
const INPUT_FOCUSED_NAME = 'customer';
const DIAL_BUTTON_NAME = 'call';
const BACKSPACE_BUTTON_NAME = 'backspace';
const ANSWER_BUTTON_NAME = 'answer';
const IGNORE_BUTTON_NAME = 'ignore';
const ALL_TAB_BUTTON_NAME = 'all';
const MISSED_TAB_BUTTON_NAME = 'missed';
const CALL_BUTTON_NAME = 'call';
const BACK_BUTTON_NAME = 'back';
const BACK_ARROW_BUTTON_NAME = 'back arrow';
const FORWARD_ARROW_BUTTON_NAME = 'forward arrow';
const DISCONNECT_BUTTON_NAME = 'disconnect';
const PLAY_RECORDING_BUTTON_NAME = 'play recording';
const PLAY_VOICEMAIL_BUTTON_NAME = 'play voicemail';
const MUTE_BUTTON_NAME = 'mute';
const TOGGLE_RECORDING_BUTTON_NAME = 'toggle recording';
const END_CALL_BUTTON_NAME = 'end call';
const REFRESH_BUTTON_NAME = 'refresh';
const TRY_AGAIN_BUTTON_NAME = 'try again';
const CANCEL_BUTTON_NAME = 'cancel';
export const RING_WIDGET_TITLE = 'Ring on HCP web';
export const VOICEMAIL_WIDGET_TITLE = 'Send to voicemail';
export const CALL_QUEUE_WIDGET_TITLE = 'Call queue';
const SEARCH_BUTTON_NAME = 'Search';
const CALL_FLOW_BUTTON_LABEL = 'call flow';
export const FORWARD_CALL_WIDGET_TITLE = 'Forward to';
export const INCOMING_CALL_TITLE = 'Incoming call';
export const END_CALL_TITLE = 'End call';
export const CALL_HOURS_WIDGET_TITLE = 'Call hours';
export const GREETING_WIDGET_TITLE = 'Play a greeting';
export const callFlowWidgetMap: CallFlowWidgetMap = {
  [END_CALL_TITLE]: 'end call',
  [INCOMING_CALL_TITLE]: 'incoming call',
  [RING_WIDGET_TITLE]: 'ring',
  [VOICEMAIL_WIDGET_TITLE]: 'voicemail',
  [FORWARD_CALL_WIDGET_TITLE]: 'forward',
  [CALL_HOURS_WIDGET_TITLE]: 'call hours',
  [GREETING_WIDGET_TITLE]: 'greeting',
  [CALL_QUEUE_WIDGET_TITLE]: 'call queue',
};
const STEP_REMOVED_TRACKING_NAME = 'Step removed';
const STEP_ADDED_TRACKING_NAME = 'Step added';
const DIALER_TOOLBAR_DROPDOWN_NAME = 'outbound call dropdown';
const VOICE_OVERVIEW_LABEL = 'voice overview';
const QUEUED_CALLS_BUTTON_NAME = 'queued calls';
const ACTIVE_CALLS_BUTTON_NAME = 'active calls';
const AGENT_MANAGEMENT_BUTTON_NAME = 'agent management';
const END_AND_ANSWER_BUTTON_NAME = 'end call & answer';
const HOLD_AND_ANSWER_BUTTON_NAME = 'hold call & answer';
const LEAVE_AND_ANSWER_BUTTON_NAME = 'leave call & answer';

const buttonClicked =
  (buttonName: string, buttonLabel: string) =>
  (attributes = {}) =>
  (dispatch: AppDispatch) => {
    dispatch(
      trackButtonClicked(buttonName, {
        button_label: buttonLabel,
        ...attributes,
      } as unknown)
    );
  };

export const trackStepRemoved = (widgetType: string) => {
  const properties = { pathname: window.location.pathname, widget: widgetType };
  return (dispatch: AppDispatch) => {
    dispatch(track(STEP_REMOVED_TRACKING_NAME, properties));
  };
};

export const trackStepAdded = (widgetType: string) => {
  const properties = { pathname: window.location.pathname, widget: widgetType };
  return (dispatch: AppDispatch) => {
    dispatch(track(STEP_ADDED_TRACKING_NAME, properties));
  };
};

export const trackIncomingCall = (call_uuid: string) => {
  const properties = { pathname: window.location.pathname, call_uuid };
  return (dispatch: AppDispatch) => {
    dispatch(track(INCOMING_CALL_TITLE, properties));
  };
};
export const trackKeypadDialogCloseClicked = buttonClicked(KEYPAD_CLOSE_BUTTON_NAME, KEYPAD_DIALOG_BUTTON_LABEL);
export const trackKeypadDialogDialClicked = buttonClicked(DIAL_BUTTON_NAME, KEYPAD_DIALOG_BUTTON_LABEL);
export const trackCallLogNavigationClicked = buttonClicked(CALL_LOG_NAVIGATION_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackKeypadNavigationClicked = buttonClicked(KEYPAD_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackCallQueueNavigationClicked = buttonClicked(CALL_QUEUE_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackVoicemailNavigationClicked = buttonClicked(VOICEMAILS_NAVIGATION_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackAnswerClicked = buttonClicked(ANSWER_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackIgnoreClicked = buttonClicked(IGNORE_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackInputFocused = buttonClicked(INPUT_FOCUSED_NAME, KEYPAD_BUTTON_LABEL);
export const trackDialClicked = buttonClicked(DIAL_BUTTON_NAME, KEYPAD_BUTTON_LABEL);
export const trackBackspaceClicked = buttonClicked(BACKSPACE_BUTTON_NAME, KEYPAD_BUTTON_LABEL);
export const trackAllTabClicked = buttonClicked(ALL_TAB_BUTTON_NAME, CALL_LOG_BUTTON_LABEL);
export const trackMissedTabClicked = buttonClicked(MISSED_TAB_BUTTON_NAME, CALL_LOG_BUTTON_LABEL);
export const trackCallClicked = buttonClicked(CALL_BUTTON_NAME, CALL_LOG_BUTTON_LABEL);
export const trackCallDetailsBackClicked = buttonClicked(BACK_BUTTON_NAME, CALL_LOG_BUTTON_LABEL);
export const trackPlayRecordingClicked = buttonClicked(PLAY_RECORDING_BUTTON_NAME, CALL_LOG_BUTTON_LABEL);
export const trackPlayVoicemailClicked = buttonClicked(PLAY_VOICEMAIL_BUTTON_NAME, CALL_LOG_BUTTON_LABEL);
export const trackMuteClicked = buttonClicked(MUTE_BUTTON_NAME, ACTIVE_DIALER_LABEL);
const trackToggleRecordingClicked = buttonClicked(TOGGLE_RECORDING_BUTTON_NAME, ACTIVE_DIALER_LABEL);
export const trackKeypadClicked = buttonClicked(KEYPAD_BUTTON_NAME, ACTIVE_DIALER_LABEL);
export const trackEndCallClicked = buttonClicked(END_CALL_BUTTON_NAME, ACTIVE_DIALER_LABEL);
export const trackKeypadBackClicked = buttonClicked(BACK_BUTTON_NAME, ACTIVE_DIALER_KEYPAD_LABEL);
export const trackAddCallClicked = buttonClicked(ADD_CALL_DIALER_TRANSFER_LABEL, ACTIVE_DIALER_LABEL);
export const trackAddCallBackClicked = buttonClicked(BACK_ARROW_BUTTON_NAME, ADD_CALL_DIALER_TRANSFER_LABEL);
export const trackConferenceViewBackClicked = buttonClicked(BACK_ARROW_BUTTON_NAME, CONFERENCE_BUTTON_LABEL);
export const trackForwardArrowClicked = buttonClicked(FORWARD_ARROW_BUTTON_NAME, CONFERENCE_BUTTON_LABEL);
export const trackConferenceDisconnectClicked = buttonClicked(DISCONNECT_BUTTON_NAME, CONFERENCE_BUTTON_LABEL);
export const trackAddCallSearchClicked = buttonClicked(SEARCH_BUTTON_NAME, ADD_CALL_DIALER_TRANSFER_LABEL);
export const trackRefreshClicked = buttonClicked(REFRESH_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackTryAgainClicked = buttonClicked(TRY_AGAIN_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackCancelClicked = buttonClicked(CANCEL_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackEndAndAnswerClicked = buttonClicked(END_AND_ANSWER_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackHoldAndAnswerClicked = buttonClicked(HOLD_AND_ANSWER_BUTTON_NAME, DIALER_BUTTON_LABEL);
export const trackLeaveAndAnswerClicked = buttonClicked(LEAVE_AND_ANSWER_BUTTON_NAME, DIALER_BUTTON_LABEL);

export const trackQueuedCallsTabClick = buttonClicked(QUEUED_CALLS_BUTTON_NAME, VOICE_OVERVIEW_LABEL);
export const trackActiveCallsTabClick = buttonClicked(ACTIVE_CALLS_BUTTON_NAME, VOICE_OVERVIEW_LABEL);
export const trackAgentManagementTabClick = buttonClicked(AGENT_MANAGEMENT_BUTTON_NAME, VOICE_OVERVIEW_LABEL);

export const trackCallFlowWidgetClicked = (buttonName: string) => (dispatch: AppDispatch) =>
  dispatch(buttonClicked(buttonName, CALL_FLOW_BUTTON_LABEL)());

export const invalidateCallLogEntities = () => (dispatch: AppDispatch) => {
  dispatch(invalidateEntities(ENTITY_CALL_LOGS_LIST, CALL_LOGS_PAGINATION_ID));
};

const DEFAULT_EXPAND_FOR_CALL_DISPOSITIONS_UPDATE = ['created_by', 'updated_by'];

export const updatedCallDispotition =
  ({ callSid, notes, reason }: UpdateParams) =>
  (dispatch: AppDispatch) =>
    CallDispositionApi.update({ callSid, notes, reason, expand: DEFAULT_EXPAND_FOR_CALL_DISPOSITIONS_UPDATE }).then(
      ({ data: newCallDisposition }: { data: VoiceCallDisposition }) => {
        dispatch(updateEntity(newCallDisposition.voice_call_log_uuid, newCallDisposition, ENTITY_CALL_DISPOSITIONS));
        dispatch(
          updateEntity(
            newCallDisposition.voice_call_log_uuid,
            {
              object: 'call_log',
              call_disposition: { object: 'call_disposition', data: newCallDisposition.voice_call_log_uuid },
            },
            ENTITY_CALL_LOGS
          )
        );
      }
    );

export const updateRecording =
  ({ callSid, isToggledOn, displayDirection }: UpdateRecordingParams) =>
  (dispatch: AppDispatch) =>
    VoiceApi.updateRecording({
      callSid,
      status: isToggledOn ? 'in-progress' : 'paused',
      displayDirection,
    }).then(() => dispatch(trackToggleRecordingClicked({ is_toggled_on: isToggledOn })));

export const updateConferenceRecording =
  ({ callSid, isToggledOn }: UpdateConferenceRecordingParams) =>
  (dispatch: AppDispatch) =>
    VoiceApi.updateConferenceRecording({ callSid, status: isToggledOn ? 'in-progress' : 'paused' }).then(() =>
      dispatch(trackToggleRecordingClicked({ is_toggled_on: isToggledOn }))
    );

export const listenVoicemail = (uuid: VoicemailUuid) => (dispatch: AppDispatch) =>
  VoicemailsApi.update({ uuid, listened: true }).then(({ data: voicemail }) =>
    dispatch(updateEntity(uuid, voicemail, ENTITY_VOICEMAILS))
  );

export const incrementUnlistenedVoicemailCount = () => (dispatch: AppDispatch, getState: () => void) => {
  const unlistenedVoicemails = getUnlistenedCount(getState());
  dispatch(
    receivedServerData(
      { unlistened_voicemails_count: unlistenedVoicemails.unlistened_voicemails_count + 1 },
      'unlistenedVoicemailsCount'
    )
  );
};

export const incrementUnacknowledgedMissedCallCount = () => (dispatch: AppDispatch, getState: () => void) => {
  const unacknowledgedMissedCallCount = getUnacknowledgedMissedCallCount(getState());
  dispatch(
    receivedServerData(
      { unacknowledged_missed_call_count: unacknowledgedMissedCallCount + 1 },
      'unacknowledgedMissedCallCount'
    )
  );
};

export const fetchVoiceSettings = () => async (dispatch: AppDispatch, _: () => void) => {
  const { data: settings } = await VoiceSettingsApi.show();
  dispatch(updateEntity(VOICE_SETTINGS_ID_ATTRIBUTE, settings, ENTITY_VOICE_SETTINGS));

  return settings;
};

export const createAddedCallLabel = (employee: VoiceDevice) => employee.identity;

export const DIALER_TOOLBAR_DROPDOWN_TRACKING = {
  buttonName: DIALER_TOOLBAR_DROPDOWN_NAME,
  buttonLabel: DIALER_BUTTON_LABEL,
};
